import PageHeader from "../components/PageHeader";
import SEOMeta from "../components/SEOMeta";

const SECTORS = [
  {
    n: "01",
    slug: "mining-and-minerals",
    title: "Mining and minerals",
    image: "https://static.prod-images.emergentagent.com/jobs/4fea0d30-cc73-4632-ad73-7645a07e6296/images/b07ebd17743a37279461064f485128ea18a46f7c08a2a464c99950212200cc4e.png",
    context:
      "Africa's mining sector sits between national ambition and global capital. Strategy in this sector must hold both in view: the state's legitimate expectation of value capture and stewardship, and the operator's need for predictable returns over capital-intensive, long-cycle investments. We advise national mining companies, ministries of mines, regulators, and operating companies on commercial strategy, mandate, and reform.",
    engagements: [
      "Strategy and operating model for a national mining company.",
      "Mandate and statutory review for a minerals regulator.",
      "Local content and supplier-development strategy for a producing operation.",
      "Commercial review of state-owned mineral marketing entity."
    ]
  },
  {
    n: "02",
    slug: "oil-and-gas",
    title: "Oil and gas",
    image: "https://images.pexels.com/photos/87236/pexels-photo-87236.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    context:
      "We advise on the strategic and commercial questions that East Africa's petroleum economy presents — from upstream commercial structures to midstream development, from national oil company mandate to fiscal optimisation. Our work is informed by the long timeframes, public scrutiny, and capital intensity that define this sector.",
    engagements: [
      "National oil company strategy, mandate, and operating model.",
      "Commercial advisory on midstream and downstream investments.",
      "Fiscal and commercial term review and benchmarking.",
      "Strategy support to ministry-level petroleum policy."
    ]
  },
  {
    n: "03",
    slug: "state-owned-enterprises",
    title: "State-owned enterprises and parastatals",
    image: "https://static.prod-images.emergentagent.com/jobs/4fea0d30-cc73-4632-ad73-7645a07e6296/images/90dc5fb38b5a5f5485285f55a07e0edf2304ad47e8dfc94291b7371c66b48af1.png",
    context:
      "State-owned enterprises occupy a particular place in the African economy — accountable to government, expected to behave commercially, and watched closely by the public. Our SOE practice is built for that reality. We support reform programmes, mandate reviews, and the difficult work of making a state-owned organisation perform without losing the legitimacy that comes from its public character.",
    engagements: [
      "Mandate review and target operating model for a parastatal.",
      "Performance contract design between SOE and shareholder ministry.",
      "Governance and board effectiveness assessment.",
      "Restructuring or commercialisation programme design."
    ]
  },
  {
    n: "04",
    slug: "infrastructure-and-utilities",
    title: "Infrastructure and utilities",
    image: "https://images.pexels.com/photos/8272123/pexels-photo-8272123.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    context:
      "We support strategy in the regulated and capital-intensive sectors that underpin the economy — energy generation and distribution, water, transport, and digital infrastructure. Our work in this area is grounded in the regulatory and public-finance context that distinguishes infrastructure from commercial business.",
    engagements: [
      "Corporate strategy for a utility company.",
      "Tariff and revenue model strategy review.",
      "Operating model and asset programme design."
    ]
  },
  {
    n: "05",
    slug: "industrial-and-extractive-adjacent",
    title: "Industrial and extractive-adjacent",
    image: "https://static.prod-images.emergentagent.com/jobs/4fea0d30-cc73-4632-ad73-7645a07e6296/images/c82e4788c121e4be89d0a8ce0a5b30c97f91061271bc690cba1af1245b7d71d2.png",
    context:
      "Many of the most strategic businesses on the continent sit alongside the resource economy — sand and aggregates, cement, fabrication, logistics, and services to the extractive sector. We advise these businesses on commercial strategy, scaling, and the operational discipline that distinguishes a regional player from a national one.",
    engagements: []
  }
];

export default function Sectors() {
  return (
    <div data-testid="sectors-page">
      <SEOMeta
        title="Sectors"
        description="Dugavu Ventures delivers global consulting across mining and minerals, oil and gas, state-owned enterprises, infrastructure, and industrial sectors in Africa."
        url="/sectors"
      />
      <PageHeader
        eyebrow="Sectors"
        title="Our sectors are chosen, not opportunistic."
        lede="Each reflects work where strategy, governance, and execution meet under public scrutiny — and where the right advisor can be the difference between a plan and a delivered result."
        image="https://images.pexels.com/photos/162568/oil-pump-jack-sunset-clouds-silhouette-162568.jpeg?auto=compress&cs=tinysrgb&w=1800&q=80"
        imageAlt="Resource-sector operation at sunset"
        testid="sectors-header"
      />

      <section className="py-16 md:py-20">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          {SECTORS.map((s, i) => (
            <article
              key={s.n}
              id={s.slug}
              className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 py-16 md:py-24 scroll-mt-24 ${i !== 0 ? "border-t border-rule" : ""}`}
              data-testid={`sector-${s.n}`}
            >
              <div className={`md:col-span-5 ${i % 2 === 1 ? "md:order-2" : ""}`}>
                <div className="aspect-[4/5] overflow-hidden bg-earth-dark/20">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover photo-treat"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className={`md:col-span-7 ${i % 2 === 1 ? "md:order-1" : ""}`}>
                <div className="flex items-baseline gap-6 mb-6">
                  <span className="numeral text-3xl">{s.n}</span>
                  <span className="text-[11px] tracking-widest uppercase font-mono text-slate">Sector {s.n}</span>
                </div>
                <h2 className="display-2 text-3xl md:text-4xl lg:text-5xl text-ink leading-tight">{s.title}</h2>
                <p className="mt-6 text-lg text-charcoal leading-relaxed">{s.context}</p>

                <div className="mt-10">
                  {s.engagements.length > 0 && (
                    <>
                      <div className="text-[11px] tracking-widest uppercase font-mono text-slate mb-5">Indicative engagements</div>
                      <ul className="space-y-3 marker-line">
                        {s.engagements.map((e) => (
                          <li key={e} className="text-charcoal text-[15px] leading-relaxed">{e}</li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
